import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors } from '@angular/forms';
import { RuleOperator } from '@pdqdev/pdq-shared/dist/entities/Rules';
import {
  DiscountRejectionCouponExtension,
  CheckoutExtensionUI,
  CouponType,
} from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { DropdownOption } from '@app/storybook/components/dropdown/dropdown.component';
import { TypedRuleGroup } from '@app/sharedModule/components/rules-dynamic-form/model';

export enum DiscountRejectionCouponTab {
  CONFIGURATIONS = 'configurations',
  SETTINGS = 'settings',
  DISPLAY_CONDITIONS = 'display_conditions',
  APPEARANCE = 'appearance',
}

export interface WidgetAppearanceFormGroup {
  backgroundColor: FormControl<CheckoutExtensionUI.Appearance>;
  textColor: FormControl<DiscountRejectionCouponExtension.BannerTextColorOptions>;
}

export interface ButtonAppearanceFormGroup {
  buttonCtaText: FormControl<string>;
  buttonAppearance: FormControl<CheckoutExtensionUI.Appearance>;
}

export interface AppearanceFormGroup {
  widgetType: FormControl<DiscountRejectionCouponExtension.WidgetType>;
  widgetAppearance: FormGroup<WidgetAppearanceFormGroup>;
  buttonAppearance: FormGroup<ButtonAppearanceFormGroup>;
}

export interface ConfigurationFormGroup {
  ruleTitle: FormControl<string>;
  active: FormControl<boolean>;
  couponType: FormControl<CouponType>;
  couponRule: FormControl<RuleOperator>;
  couponCode: FormControl<string>;
  discountValue: FormControl<number>;
  couponExpiryDays: FormControl<number>;
  autoApply: FormControl<boolean>;
  combinesWithProductDiscounts: FormControl<boolean>;
  combinesWithOrderDiscounts: FormControl<boolean>;
  combinesWithShippingDiscounts: FormControl<boolean>;
  messageTitle: FormControl<string>;
  messageDescription: FormControl<string>;
  replacementDiscountRule: FormControl<DiscountRejectionCouponExtension.ReplacementRule>;
  storeDiscount: FormControl<string>;
}

export interface FailedAttemptThresholdFormGroup {
  enabled: FormControl<boolean>;
  attempts: FormControl<number>;
  limitWithinTimeWindow: FormControl<boolean>;
  timeWindowValue: FormControl<number>;
  timeWindowUnit: FormControl<DiscountRejectionCouponExtension.TimeWindowUnit>;
}

export interface DiscountRejectionCouponFormGroup {
  usageToken: FormControl<string>;
  internalName: FormControl<string>;
  internalDescription: FormControl<string>;
  configurations: FormArray<FormGroup<ConfigurationFormGroup>>;
  appearance: FormGroup<AppearanceFormGroup>;
  displayRules: FormArray<FormGroup<TypedRuleGroup>>;
  failedAttemptThreshold: FormGroup<FailedAttemptThresholdFormGroup>;
}

export interface RadioOption<T = unknown> {
  value: T;
  text: string;
}

export interface CheckboxConfig {
  controlName: string;
  label: string;
}

export interface DropdownFieldConfig {
  type: 'dropdown';
  title: string;
  controlName: string;
  options: DropdownOption[];
}

export interface InputFieldConfig {
  type: 'input';
  label: string;
  placeholder: string;
  controlName: string;
}

export type FormFieldConfig = DropdownFieldConfig | InputFieldConfig;

export function isRichTextEmpty(value: string | null | undefined): boolean {
  if (!value) return true;
  const textOnly = value
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, '')
    .trim();
  return textOnly.length === 0;
}

export function configurationGroupValidator(group: AbstractControl): ValidationErrors | null {
  const errors: ValidationErrors = {};

  const couponRule = group.get('couponRule')?.value;
  const couponCode = group.get('couponCode')?.value;
  if ((couponRule === RuleOperator.IS || couponRule === RuleOperator.INCLUDE) && !couponCode) {
    errors.couponCodeRequired = true;
  }
  const messageTitle = group.get('messageTitle')?.value;
  const messageDescription = group.get('messageDescription')?.value;

  if (isRichTextEmpty(messageTitle) && isRichTextEmpty(messageDescription)) {
    errors.messageRequired = true;
  }

  return Object.keys(errors).length ? errors : null;
}
